import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

interface SelectionContextValue {
  /** Set of selected request IDs */
  selectedIds: Set<string>;
  /** Last selected ID (for shift-click range selection) */
  lastSelectedId: string | null;
  /** Toggle a single item's selection */
  toggleSelection: (id: string) => void;
  /** Add item to selection (for Cmd/Ctrl+Click) */
  addToSelection: (id: string) => void;
  /** Remove item from selection */
  removeFromSelection: (id: string) => void;
  /** Select a range of items (for Shift+Click) */
  selectRange: (id: string, allIds: string[]) => void;
  /** Select a single item (clears others) */
  selectSingle: (id: string) => void;
  /** Clear all selections */
  clearSelection: () => void;
  /** Check if an item is selected */
  isSelected: (id: string) => boolean;
  /** Get count of selected items */
  selectionCount: number;
  /** Get array of selected IDs */
  selectedArray: string[];
}

const SelectionContext = createContext<SelectionContextValue | null>(null);

/**
 * Hook to access selection context
 */
export const useSelectionContext = () => {
  const context = useContext(SelectionContext);
  if (!context) {
    throw new Error('useSelectionContext must be used within a SelectionProvider');
  }
  return context;
};

/**
 * Optional hook that returns null if outside provider (for components that may or may not have selection)
 */
export const useOptionalSelectionContext = () => {
  return useContext(SelectionContext);
};

interface SelectionProviderProps {
  children: React.ReactNode;
}

/**
 * Provider for multi-select functionality in the queue browser
 */
export const SelectionProvider: React.FC<SelectionProviderProps> = ({ children }) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [lastSelectedId, setLastSelectedId] = useState<string | null>(null);

  const toggleSelection = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    setLastSelectedId(id);
  }, []);

  const addToSelection = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    setLastSelectedId(id);
  }, []);

  const removeFromSelection = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const selectRange = useCallback(
    (id: string, allIds: string[]) => {
      if (!lastSelectedId) {
        // No previous selection, just select this one
        setSelectedIds(new Set([id]));
        setLastSelectedId(id);
        return;
      }

      const startIndex = allIds.indexOf(lastSelectedId);
      const endIndex = allIds.indexOf(id);

      if (startIndex === -1 || endIndex === -1) {
        // One of the IDs not found, just select the clicked one
        setSelectedIds(new Set([id]));
        setLastSelectedId(id);
        return;
      }

      const start = Math.min(startIndex, endIndex);
      const end = Math.max(startIndex, endIndex);
      const rangeIds = allIds.slice(start, end + 1);

      setSelectedIds(prev => {
        const next = new Set(prev);
        rangeIds.forEach(rangeId => next.add(rangeId));
        return next;
      });
      // Don't update lastSelectedId for range selection
    },
    [lastSelectedId]
  );

  const selectSingle = useCallback((id: string) => {
    setSelectedIds(new Set([id]));
    setLastSelectedId(id);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setLastSelectedId(null);
  }, []);

  const isSelected = useCallback((id: string) => selectedIds.has(id), [selectedIds]);

  const selectionCount = selectedIds.size;
  const selectedArray = useMemo(() => Array.from(selectedIds), [selectedIds]);

  const value: SelectionContextValue = useMemo(
    () => ({
      selectedIds,
      lastSelectedId,
      toggleSelection,
      addToSelection,
      removeFromSelection,
      selectRange,
      selectSingle,
      clearSelection,
      isSelected,
      selectionCount,
      selectedArray,
    }),
    [
      selectedIds,
      lastSelectedId,
      toggleSelection,
      addToSelection,
      removeFromSelection,
      selectRange,
      selectSingle,
      clearSelection,
      isSelected,
      selectionCount,
      selectedArray,
    ]
  );

  return <SelectionContext.Provider value={value}>{children}</SelectionContext.Provider>;
};
